import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { allMovies, movieGenres, getMoviesByGenre } from '@/mocks/movies';
import { allTVShows, tvGenres, getTVShowsByGenre } from '@/mocks/tvshows';
import { getPosterUrl } from '@/mocks/imageUrls';

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const isMovie = type === 'movie';
  const [activeGenre, setActiveGenre] = useState('全部');
  const [sortBy, setSortBy] = useState<'latest' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [type]);

  const genres = isMovie ? movieGenres : tvGenres;
  const allData = isMovie ? allMovies : allTVShows;
  const getByGenre = isMovie ? getMoviesByGenre : getTVShowsByGenre;

  const filteredData = useMemo(() => {
    const byGenre = getByGenre(activeGenre);
    return [...byGenre].sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    });
  }, [activeGenre, sortBy, getByGenre]);

  const title = isMovie ? '最新电影' : '最新电视剧';
  const description = isMovie
    ? '最新电影新片上映同步更新快，高清免费在线观看'
    : '最新电视剧热门剧集每日更新，追剧不等待';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: title },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{title}</h1>
              <p className="text-foreground-500 text-sm mt-2">{description}</p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                {genres.map((genre) => (
                  <button
                    key={genre}
                    onClick={() => setActiveGenre(genre)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeGenre === genre
                        ? 'bg-primary-500 text-background-950'
                        : 'bg-background-800 text-foreground-600 hover:text-foreground-800 hover:bg-background-700'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-background-800 rounded-full p-1">
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-4 py-1.5 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === 'latest' ? 'bg-primary-500 text-background-950' : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  最新
                </button>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-4 py-1.5 text-sm rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === 'rating' ? 'bg-primary-500 text-background-950' : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  评分最高
                </button>
              </div>
            </div>

            {filteredData.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-800">
                  <i className="ri-movie-2-line text-3xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无相关内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-16">
                {filteredData.map((item) => (
                  <MovieCard
                    key={item.id}
                    {...item}
                    posterUrl={getPosterUrl(item.posterSeq)}
                    episodes={'episodes' in item ? item.episodes : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}